import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import {
  Upload,
  FileText,
  Camera,
  Check,
  X,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Image as ImageIcon,
  Info,
  Car,
} from "lucide-react";
import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyHeader,
  ExtendedWarrantyCard,
  ExtendedWarrantyButton,
} from "./ExtendedWarrantyScreenWrapper";
import { useAuth } from "../../hooks/useAuth";
import insurancePolicyService from "../../lib/InsurancePolicyService";
import analyticsService from "../../lib/AnalyticsService";

// Upload stages configuration
const UPLOAD_STAGES = [
  { id: "setting-up", label: "Setting up", duration: 2000, image: "/upload/SETTING-UP.gif" },
  { id: "uploading", label: "Uploading document", duration: 3000, image: "/upload/UPLOADING.gif" },
  { id: "parsing", label: "Parsing document", duration: 2500, image: "/upload/PARSING.gif" },
  { id: "extracting", label: "Extracting data with AI", duration: 4000, image: "/upload/EXTRACTING.gif" },
  { id: "organizing", label: "Organizing your info", duration: 1500, image: "/upload/ORGANIZING.gif" },
];

const ExtendedWarrantyPolicyUploadScreen = ({ flow, onComplete, onBack }) => {
  const { user, getIdToken } = useAuth();
  const [existingPolicy, setExistingPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [extractedVehicles, setExtractedVehicles] = useState([]);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const uploadResultRef = useRef(null);
  const stageTimersRef = useRef([]);

  const flowType = flow === "Refinancing" ? "refinancing" : "extended-warranty";

  useEffect(() => {
    analyticsService.trackPageView("ew_policy_upload", {
      screen: "policy_upload",
      flow_type: flowType,
    });
  }, [flowType]);

  useEffect(() => {
    // Check if user has existing policies
    const fetchPolicies = async () => {
      try {
        if (!user || !user.uid) {
          setError("User authentication required");
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);
        const token = await getIdToken();

        // Use the policy service to check for existing policies
        const result = await insurancePolicyService.checkExistingPolicies(
          token
        );

        if (result) {
          setExistingPolicy(result.policy);
          analyticsService.trackEvent("Extended Warranty Existing Policy Found", {
            has_existing_policy: true,
            screen: "policy_upload",
            flow_type: flowType,
          });
        }
        setLoading(false);
      } catch (err) {
        console.error("Error checking existing policies:", err);
        analyticsService.trackError("ew_policy_check_error", err, { screen: "policy_upload", flow_type: flowType });
        setError("Unable to check for existing policies. Please try again.");
        setLoading(false);
      } finally {
      }
    };
    // Fetch policies when component mounts or user changes
    fetchPolicies();
  }, [user]);

  // Upload stage state (null = not uploading, or stage id)
  const [uploadStage, setUploadStage] = useState(null);

  const [uploadedFileUrl, setUploadedFileUrl] = useState(() => {
    // Try to restore from session storage on component mount
    const cached = sessionStorage.getItem("cachedInsuranceFileUrl");
    return cached ? JSON.parse(cached) : null;
  });

  // Detect if the user is on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const validateFile = (file) => {
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB

    // Allow images and PDFs for insurance declaration documents
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      throw new Error("Please upload an image or PDF file");
    }

    if (file.size > MAX_SIZE) {
      throw new Error("File size must be less than 10MB");
    }
  };

  const handleFileSelect = (file) => {
    try {
      validateFile(file);
      setSelectedFile(file);

      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        // For PDFs, just show file info
        setPreview(null);
      }

      setError(null);
      setUploadSuccess(false);
      setExtractedVehicles([]);
    } catch (err) {
      setError(err.message);
      setSelectedFile(null);
      setPreview(null);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files?.[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleCameraCapture = () => {
    if (isMobile && cameraInputRef.current) {
      cameraInputRef.current.click();
    } else {
      setError(
        "Camera works best on mobile devices. You may continue if your device has a camera, or use the file upload option instead."
      );
      setTimeout(() => {
        if (cameraInputRef.current) {
          cameraInputRef.current.click();
        }
      }, 1500);
    }
  };

  // Clear all stage timers
  const clearStageTimers = () => {
    stageTimersRef.current.forEach((timer) => clearTimeout(timer));
    stageTimersRef.current = [];
  };

  // Start the staged progress animation
  const startStageProgress = () => {
    clearStageTimers();
    let cumulativeDelay = 0;

    UPLOAD_STAGES.forEach((stage) => {
      const timer = setTimeout(() => {
        setUploadStage(stage.id);
      }, cumulativeDelay);
      stageTimersRef.current.push(timer);
      cumulativeDelay += stage.duration;
    });
  };

  const handleUpload = async () => {
    try {
      if (!selectedFile) {
        throw new Error("Please select a file first");
      }

      setUploadStage("setting-up");
      setError(null);
      uploadResultRef.current = null;

      analyticsService.trackEvent("Extended Warranty Upload Initiated", {
        file_type: selectedFile.type,
        file_size_bytes: selectedFile.size,
        screen: "policy_upload",
        flow_type: flowType,
      });

      // Track when we started for timing calculations
      const startTime = Date.now();

      // Start the staged progress animation
      startStageProgress();

      // Get Firebase token for authentication
      const token = await getIdToken();

      let result;
      // Process the insurance policy document using the service
      if (flow === "ExtendedWarranty") {
        result = await insurancePolicyService.processPolicyDocumentForExtendedWarranty(
          selectedFile,
          token
        );
      } else {
        result = await insurancePolicyService.processPolicyDocumentForRefinancing(
          selectedFile,
          token
        );
      }

      // Store the result
      uploadResultRef.current = result;

      if (result.fileUrl) {
        const fileUrlData = {
          url: result.fileUrl,
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          uploadedAt: new Date().toISOString(),
        };

        sessionStorage.setItem(
          "cachedInsuranceFileUrl",
          JSON.stringify(fileUrlData)
        );
        setUploadedFileUrl(fileUrlData);

        console.log("File URL cached:", result.fileUrl);
      }

      if (!result.success) {
        throw new Error(result.error || "Upload failed");
      }

      // Calculate how much time has elapsed and how much remains
      const elapsedTime = Date.now() - startTime;
      const totalStageDuration = UPLOAD_STAGES.reduce((sum, s) => sum + s.duration, 0);
      const remainingTime = Math.max(0, totalStageDuration - elapsedTime);

      // Wait for remaining stages to finish, then show result
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      console.log("Upload successful:", result);
      analyticsService.trackFileUpload(selectedFile.name, selectedFile.size, selectedFile.type, true, null);

      // Store submission info in session storage
      const uploadData = {
        type: "insurance_declaration_upload",
        timestamp: new Date().toISOString(),
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        result: result.result,
        fileUrl: result.fileUrl,
      };

      clearStageTimers();
      setUploadStage(null);
      setUploadSuccess(true);

      // Wait a moment to show success message before completing
      setTimeout(() => {
        if (onComplete) {
          onComplete(uploadData);
        }
      }, 2000);
    } catch (err) {
      console.error("Upload error:", err);
      analyticsService.trackFileUpload(selectedFile?.name, selectedFile?.size, selectedFile?.type, false, err);
      clearStageTimers();
      setError(err.message || "An unexpected error occurred");
      setUploadStage(null);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    setUploadSuccess(false);
    setExtractedVehicles([]);
  };

  return (
    <ExtendedWarrantyScreenWrapper>
      {/* Translucent loading overlay with stage GIFs */}
      {uploadStage && (() => {
        const currentStage = UPLOAD_STAGES.find((s) => s.id === uploadStage);
        const currentIndex = UPLOAD_STAGES.findIndex((s) => s.id === uploadStage);
        return (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center z-50">
            <div className="relative w-[90%] max-w-md">
              {currentStage?.image && (
                <img
                  src={currentStage.image}
                  alt={currentStage.label}
                  className="w-full rounded-2xl object-contain"
                />
              )}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                <div className="bg-white/80 backdrop-blur-md rounded-full px-6 py-2 shadow-lg text-center">
                  <p className="text-sm font-semibold text-gray-600 whitespace-nowrap">
                    {currentStage?.label}...
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              {UPLOAD_STAGES.map((stage, index) => (
                <div
                  key={stage.id}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index <= currentIndex
                      ? "bg-purple-600 w-6"
                      : "bg-gray-300 w-2"
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-white/80 mt-2">
              Please don't close this window
            </p>
          </div>
        );
      })()}

      {/* Back button */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )}

      {loading ? (
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6 flex flex-col items-center justify-center min-h-40">
              <div className="w-10 h-10 border-4 border-t-transparent border-blue-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">Checking for existing policies...</p>
            </CardContent>
          </Card>
        </div>
      ) : existingPolicy ? (
        // Show existing policy found
        <>
          <ExtendedWarrantyCard variant="success">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="font-medium text-green-900">
                  Policy Document Already Uploaded
                </p>
                <p className="text-sm text-green-700 mt-1">
                  {existingPolicy.provider &&
                    `Provider: ${existingPolicy.provider}`}
                  {existingPolicy.policyNumber &&
                    `, Policy #: ${existingPolicy.policyNumber
                      .slice(-4)
                      .padStart(existingPolicy.policyNumber.length, "*")}`}
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Uploaded on{" "}
                  {new Date(existingPolicy.checkedInAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </ExtendedWarrantyCard>

          <ExtendedWarrantyButton
            onClick={() => onComplete && onComplete(existingPolicy)}
            variant="primary"
          >
            <ArrowRight className="w-4 h-4" />
            <span>Continue</span>
          </ExtendedWarrantyButton>
        </>
      ) : (
        <>
          {/* Header */}
          <ExtendedWarrantyHeader
            title="Upload your Proof of Insurance"
            subtitle="This allows us to pre-fill and verify your vehicle info automatically."
          />

          {/* What we'll extract info card */}
          <ExtendedWarrantyCard variant="info">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  What is a Proof of Insurance Document:
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>You can upload your insurance declaration form</li>
                  <li>You can upload your insurance card</li>
                </ul>
              </div>
            </div>
          </ExtendedWarrantyCard>

          {/* Upload Area */}
          <ExtendedWarrantyCard>
            {!selectedFile ? (
              <div className="space-y-4">
                {/* Hidden file inputs */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInput}
                  accept="image/*,application/pdf"
                  className="hidden"
                />
                <input
                  type="file"
                  ref={cameraInputRef}
                  onChange={handleFileInput}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />

                {/* Upload options */}
                <div
                  className={
                    isMobile
                      ? "grid grid-cols-2 gap-4"
                      : "flex justify-center gap-4"
                  }
                >
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Upload className="w-6 h-6 text-blue-500" />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-900">
                        Choose File
                      </div>
                      <div className="text-sm text-gray-600">PDF or Image</div>
                    </div>
                  </button>

                  {isMobile && (
                    <button
                      onClick={handleCameraCapture}
                      className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Camera className="w-6 h-6 text-green-500" />
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-gray-900">
                          Take Photo
                        </div>
                        <div className="text-sm text-gray-600">Use Camera</div>
                      </div>
                    </button>
                  )}
                </div>

                <p className="text-sm text-gray-500 text-center">
                  {isMobile
                    ? "You can take a photo or upload from your device"
                    : "Upload a PDF or image document (max 10MB)"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* File preview */}
                <div className="relative">
                  {preview ? (
                    // Image preview
                    <img
                      src={preview}
                      alt="Document preview"
                      className="w-full max-h-64 object-contain rounded-lg border"
                    />
                  ) : (
                    // PDF preview (show file info)
                    <div className="p-6 border rounded-lg bg-gray-50 flex flex-col items-center justify-center">
                      <FileText className="w-16 h-16 text-blue-500 mb-3" />
                      <span className="font-medium text-gray-900 text-center break-all">
                        {selectedFile.name}
                      </span>
                      <span className="text-sm text-gray-500 mt-1">
                        PDF Document (
                        {(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                      </span>
                    </div>
                  )}

                  <button
                    onClick={removeFile}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-lg hover:bg-gray-100"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* Success message and extracted vehicles */}
                {uploadSuccess && (
                  <div className="space-y-4">
                    <div className="flex items-start gap-2 p-3 bg-green-50 text-green-800 rounded-lg text-sm">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <p>
                        Declaration page uploaded successfully! We've extracted
                        the following vehicle information:
                      </p>
                    </div>

                    {/* Extracted vehicles display */}
                    {extractedVehicles.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-900">
                          Extracted Vehicles:
                        </h4>
                        {extractedVehicles.map((vehicle, index) => (
                          <div
                            key={vehicle.id || index}
                            className="p-4 border rounded-lg bg-white"
                          >
                            <div className="flex items-start gap-3">
                              <Car className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 space-y-1">
                                <div className="font-medium text-gray-900">
                                  {vehicle.year} {vehicle.make} {vehicle.model}
                                </div>
                                {vehicle.vin && (
                                  <div className="text-sm text-gray-600">
                                    VIN: {vehicle.vin}
                                  </div>
                                )}
                                {vehicle.licensePlate && (
                                  <div className="text-sm text-gray-600">
                                    License Plate: {vehicle.licensePlate}
                                  </div>
                                )}
                                <div className="flex items-center gap-1 mt-2">
                                  <Check className="w-4 h-4 text-green-500" />
                                  <span className="text-xs text-green-600 font-medium">
                                    High confidence extraction
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* File info */}
                {!uploadSuccess && (
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {selectedFile.name} ready for upload
                    </span>
                  </div>
                )}
              </div>
            )}
          </ExtendedWarrantyCard>

          {/* Error display */}
          {error && (
            <ExtendedWarrantyCard variant="error">
              <div className="flex items-start gap-2 text-red-600">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            </ExtendedWarrantyCard>
          )}

          {/* Upload button */}
          {selectedFile && !uploadSuccess && (
            <ExtendedWarrantyButton
              onClick={handleUpload}
              disabled={uploadStage !== null}
            >
              {uploadStage ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Upload</span>
                </>
              )}
            </ExtendedWarrantyButton>
          )}

          {/* Continue button after successful upload */}
          {uploadSuccess && extractedVehicles.length > 0 && (
            <ExtendedWarrantyButton
              onClick={() =>
                onComplete &&
                onComplete({
                  type: "insurance_declaration_upload",
                  timestamp: new Date().toISOString(),
                  fileName: selectedFile.name,
                  extractedVehicles: extractedVehicles,
                })
              }
              variant="primary"
            >
              <Check className="w-4 h-4" />
              <span>Continue with Extracted Vehicles</span>
            </ExtendedWarrantyButton>
          )}
        </>
      )}
      {/* Privacy note */}
      {/* <div className="text-xs text-center text-gray-500 px-4">
        Your document is securely processed and encrypted using bank-level security protocols. 
        We extract vehicle information to streamline your insurance experience.
      </div> */}
    </ExtendedWarrantyScreenWrapper>
  );
};

export default ExtendedWarrantyPolicyUploadScreen;
